import React from 'react';
import { 
  Users, UserCheck, UserPlus, Gift, TrendingUp, 
  Clock, Home, Calendar, Phone, Award, Package,
  ArrowUp, ArrowDown, Minus
} from 'lucide-react';

// KPI Card Component - Large visual stats (responsive)
export function KPICard({ 
  title, 
  value, 
  subtitle, 
  icon: Icon, 
  trend,
  trendValue,
  variant = 'default',
  onClick,
  className = '' 
}) {
  const variants = {
    default: 'bg-[#0A0A0A] border-white/10',
    highlight: 'bg-[#0F0F0F] border-white/20 shadow-[0_0_30px_rgba(255,255,255,0.05)]',
    success: 'bg-[#0A0A0A] border-emerald-500/30',
    warning: 'bg-[#0A0A0A] border-amber-500/30',
    accent: 'bg-gradient-to-br from-[#0F0F0F] to-[#0A0A0A] border-white/20'
  };

  const TrendIcon = trend === 'up' ? ArrowUp : trend === 'down' ? ArrowDown : Minus;
  const trendColor = trend === 'up' ? 'text-emerald-400' : trend === 'down' ? 'text-red-400' : 'text-zinc-500';

  return (
    <div 
      className={`
        p-4 lg:p-6 rounded-lg border transition-all duration-300
        ${variants[variant]}
        ${onClick ? 'cursor-pointer hover:border-white/30 hover:bg-[#111]' : ''}
        ${className}
      `}
      onClick={onClick}
      data-testid={`kpi-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-start justify-between mb-3 lg:mb-4">
        <div className="flex items-center gap-2 lg:gap-3 min-w-0">
          {Icon && (
            <div className="w-8 h-8 lg:w-10 lg:h-10 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
              <Icon className="w-4 h-4 lg:w-5 lg:h-5 text-zinc-400" />
            </div>
          )}
          <span className="text-[10px] lg:text-xs uppercase tracking-[0.15em] lg:tracking-[0.2em] text-zinc-500 font-medium truncate">
            {title}
          </span>
        </div>
        {trend && (
          <div className={`flex items-center gap-1 ${trendColor} flex-shrink-0`}>
            <TrendIcon className="w-3 h-3" />
            {trendValue && <span className="text-xs font-mono">{trendValue}</span>}
          </div>
        )}
      </div>
      
      <p className="text-2xl lg:text-4xl font-light text-white font-['Outfit'] tracking-tight mb-1">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </p>
      
      {subtitle && (
        <p className="text-xs lg:text-sm text-zinc-500 truncate">{subtitle}</p>
      )}
    </div>
  );
}

// Mini KPI for compact displays
export function MiniKPI({ label, value, icon: Icon, color = 'white' }) {
  const colors = {
    white: 'text-white',
    green: 'text-emerald-400',
    yellow: 'text-amber-400',
    red: 'text-red-400',
    blue: 'text-blue-400'
  };

  return (
    <div className="flex items-center gap-2 lg:gap-3 p-2 lg:p-3 rounded-lg bg-white/5">
      {Icon && <Icon className={`w-4 h-4 ${colors[color]}`} />}
      <div className="min-w-0">
        <p className={`text-base lg:text-lg font-semibold ${colors[color]} font-['Outfit']`}>{value}</p>
        <p className="text-[10px] lg:text-xs text-zinc-500 truncate">{label}</p>
      </div>
    </div>
  );
}

// KPI Grid Layout - Responsive
export function KPIGrid({ children, columns = 4, className = '' }) {
  return (
    <div 
      className={`grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 ${className}`}
    >
      {children}
    </div>
  );
}

// Status Distribution Bar - Responsive
export function StatusDistribution({ data, total }) {
  const statusColors = {
    nuevo_lead: 'bg-blue-500',
    registrado: 'bg-blue-400',
    contactado: 'bg-yellow-500',
    prueba_agendada: 'bg-orange-500',
    instalado: 'bg-purple-500',
    activo: 'bg-cyan-500',
    validado: 'bg-emerald-500',
    recompensa_entregada: 'bg-white'
  };

  const statusLabels = {
    nuevo_lead: 'Leads',
    registrado: 'Registrados',
    contactado: 'Contactados',
    prueba_agendada: 'Visitas',
    instalado: 'Instalados',
    activo: 'Activos',
    validado: 'Validados',
    recompensa_entregada: 'Completados'
  };

  return (
    <div className="space-y-3">
      <div className="flex h-2 lg:h-3 rounded-full overflow-hidden bg-white/5">
        {Object.entries(data).map(([status, count]) => {
          if (count === 0) return null;
          const percentage = (count / total) * 100;
          return (
            <div
              key={status}
              className={`${statusColors[status] || 'bg-zinc-500'} transition-all`}
              style={{ width: `${percentage}%` }}
              title={`${statusLabels[status] || status}: ${count}`}
            />
          );
        })}
      </div>
      <div className="flex flex-wrap gap-2 lg:gap-4">
        {Object.entries(data).map(([status, count]) => {
          if (count === 0) return null;
          return (
            <div key={status} className="flex items-center gap-1.5 lg:gap-2">
              <div className={`w-2 h-2 rounded-full ${statusColors[status] || 'bg-zinc-500'}`} />
              <span className="text-[10px] lg:text-xs text-zinc-400">
                {statusLabels[status] || status}: <span className="text-white font-medium">{count}</span>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Executive Summary Component - Responsive
export function ExecutiveSummary({ stats }) {
  if (!stats) return null;

  const pendingRewards = stats.status_counts?.validado || 0;
  const instalaciones = stats.status_counts?.instalado || 0;
  const pruebasAgendadas = stats.status_counts?.prueba_agendada || 0;

  return (
    <div className="space-y-4 lg:space-y-6">
      <KPIGrid>
        <KPICard
          title="Clientes activos"
          value={stats.active_clients ?? stats.total_clients}
          subtitle={`${stats.total_clients} en total`}
          icon={Users}
          variant="default"
        />
        <KPICard
          title="Total Referidos"
          value={stats.total_referrals}
          subtitle={`${stats.recent_leads_7d || 0} últimos 7d`}
          icon={UserPlus}
          trend={stats.recent_leads_7d > 0 ? 'up' : 'neutral'}
          trendValue={stats.recent_leads_7d > 0 ? `+${stats.recent_leads_7d}` : ''}
          variant="default"
        />
        <KPICard
          title="Conversión"
          value={`${stats.conversion_rate}%`}
          subtitle="Referidos validados o recompensados"
          icon={TrendingUp}
          variant="highlight"
        />
        <KPICard
          title="Recompensas"
          value={pendingRewards}
          subtitle="Por entregar"
          icon={Gift}
          variant={pendingRewards > 0 ? 'accent' : 'default'}
        />
      </KPIGrid>

      <div className="grid grid-cols-3 gap-2 lg:gap-4">
        <KPICard
          title="Instalados"
          subtitle="Referidos"
          value={instalaciones}
          icon={Home}
          variant="default"
        />
        <KPICard
          title="Con visita"
          subtitle="Referidos"
          value={pruebasAgendadas}
          icon={Calendar}
          variant="default"
        />
        <KPICard
          title="Contactados"
          subtitle="Referidos"
          value={stats.status_counts?.contactado || 0}
          icon={Phone}
          variant="default"
        />
      </div>

      {stats.total_referrals > 0 && (
        <div className="p-4 lg:p-6 rounded-lg bg-[#0A0A0A] border border-white/10">
          <h3 className="text-[10px] lg:text-sm uppercase tracking-[0.15em] lg:tracking-[0.2em] text-zinc-500 mb-3 lg:mb-4">
            Distribución por Estado
          </h3>
          <StatusDistribution 
            data={stats.status_counts || {}} 
            total={stats.total_referrals} 
          />
        </div>
      )}
    </div>
  );
}

export default KPICard;
